import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Slider, Card, CardContent, Typography, Box } from "@mui/material"; 

const Prediction = () => {
  const [config, setConfig] = useState({
    stackedPer1000: 300,
    collectedPer1000: 300,
    timeSavedStack: 10,
    timeSavedRetrieval: 15,
    timeSavedMessaging: 15,
    timeSavedAddScript: 15,
  });

  const [proScriptPercentage, setProScriptPercentage] = useState(50);
  const [acutePercentage, setAcutePercentage] = useState(20);
  const [itemsPerMonth, setItemsPerMonth] = useState(10000);

  const calculateBarValue = (per1000, timeSaved) => {
    return ((itemsPerMonth / 1000) * (per1000 * timeSaved / 3600)) / 4; 
  };

  const calculateMessagesBarValue = () => {
    const totalMessages = (itemsPerMonth / 1000) * (proScriptPercentage / 100) * config.stackedPer1000;
    return ((totalMessages * config.timeSavedMessaging) / 3600) / 4;
  };

  const nonAcutePercentage = 100 - acutePercentage;

  const data = [
    { name: "Stacking", value: calculateBarValue(config.stackedPer1000, config.timeSavedStack) * (nonAcutePercentage / 100) },
    { name: "Retrieval", value: calculateBarValue(config.collectedPer1000, config.timeSavedRetrieval) * (nonAcutePercentage / 100) },
    { name: "Messaging", value: calculateMessagesBarValue() * (nonAcutePercentage / 100) },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value.toFixed(1);
      return (
        <div style={{ backgroundColor: "white", padding: "5px", border: "1px solid #ccc" }}>
          <p>{`Hrs saved every week: ${value}`}</p>
        </div>
      );
    }
    return null;
  };

  const totalSavings = data.reduce((total, currentBar) => total + currentBar.value, 0).toFixed(1);

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      height: "100vh", 
      padding: "20px", 
      backgroundColor: "#f5f5f5" // Pale grey background 
    }}>
      {/* Header */}
      <Typography variant="h4" textAlign="center" mb={2}>
        Pharmace - How much time does it save?
      </Typography>

      <Box sx={{ display: "flex", flexGrow: 1, marginTop: "2cm" }}> {/* Move content down */}
        {/* Left: Graph */}
        <Box sx={{ flex: 0.7, padding: "20px" }}> {/* Increased size */}
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" textAlign="center" mb={2}>
                Total Hours saved per week: {totalSavings}
              </Typography>
              <ResponsiveContainer width="100%" height={500}> {/* Increased graph height */}
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: "Hours", angle: -90, position: "insideLeft" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#4A90E2" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Right: Sliders */}
        <Box sx={{ flex: 0.5, padding: "20px" }}> {/* Increased size */}
          <Card sx={{ height: "100%", overflowY: "auto" }}>
            <CardContent>
              <Typography variant="h6" textAlign="center" mb={2}>
                Adjust Parameters to reflect your pharmacy
              </Typography>

              {/* Slider Component */}
              {[
                { label: "Items per month", value: itemsPerMonth, min: 3000, max: 25000, step: 100, onChange: setItemsPerMonth },
                { label: "% of acutes", value: acutePercentage, min: 0, max: 100, step: 1, onChange: setAcutePercentage },
                { label: "% sent messages", value: proScriptPercentage, min: 0, max: 100, step: 1, onChange: setProScriptPercentage },
                { label: "Time to send a message (sec)", value: config.timeSavedMessaging, min: 5, max: 30, step: 1, onChange: (val) => setConfig({ ...config, timeSavedMessaging: val }) },
                { label: "Time to retrieve an item from box(sec)", value: config.timeSavedRetrieval, min: 5, max: 45, step: 1, onChange: (val) => setConfig({ ...config, timeSavedRetrieval: val }) },
                { label: "Time to add a script to box (sec)", value: config.timeSavedStack, min: 5, max: 30, step: 1, onChange: (val) => setConfig({ ...config, timeSavedStack: val }) },
              ].map(({ label, value, min, max, step, onChange }, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography sx={{ width: "60%" }}>{label}</Typography>
                  <Slider
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e, newValue) => onChange(newValue)}
                    sx={{ flex: 1 }}
                  />
                  <Typography sx={{ width: "50px", textAlign: "right" }}>{value} </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Prediction;
